import {
  Box,
  Drawer,
  styled,
  SwipeableDrawer,
  type SwipeableDrawerProps,
} from '@mui/material';
import React, { type ReactNode } from 'react';

import { useDevice } from '@/hooks';

// Make onOpen optional since it's not needed for the Desktop Drawer
export type ResponsiveDrawerProps = Omit<SwipeableDrawerProps, 'onOpen'> & {
  onOpen?: React.ReactEventHandler;
  header?: () => ReactNode;
  footer?: () => ReactNode;
  showPuller?: boolean;
};

const Puller = styled(Box)(({ theme }) => ({
  width: 36,
  height: 5,
  backgroundColor: theme.palette.text.secondary,
  borderRadius: 3,
  position: 'absolute',
  top: 6,
  left: 'calc(50% - 18px)',
  zIndex: 1,
}));

export const ResponsiveDrawer: React.FC<ResponsiveDrawerProps> = ({
  children,
  onOpen = () => {},
  PaperProps,
  header,
  footer,
  showPuller = true,
  ...props
}) => {
  const { isMobile } = useDevice();

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        onOpen={onOpen}
        disableSwipeToOpen
        PaperProps={{
          ...PaperProps,
          elevation: 0,
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            // Ensure logic for max height if needed, but let's keep it extensible
            // Typically mobile drawers shouldn't take full screen automatically unless specified
            maxHeight: '90dvh', // Dynamic viewport height
            overflow: 'hidden', // Prevent outer scrolling so flex container handles it
            display: 'flex',
            flexDirection: 'column',
            ...PaperProps?.sx,
          },
        }}
        {...props}
      >
        {showPuller && <Puller />}
        {/* Add padding top to account for Puller */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0, // Critical for nested flex scrolling
          }}
        >
          {header?.()}
          <Box sx={{ flex: 1, overflow: 'auto' }}>{children}</Box>
          {footer?.()}
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Drawer
      anchor="right"
      PaperProps={{
        ...PaperProps,
        elevation: 0,
      }}
      {...props}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {header?.()}
        <Box sx={{ flex: 1, overflow: 'auto' }}>{children}</Box>
        {footer?.()}
      </Box>
    </Drawer>
  );
};
